/* eslint-disable import/order -- Preserve order to avoid circular dependency (Error → MainLayout → routes) */
import React, { useEffect, useState } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { ThemeProvider, Grid } from '@mui/material';
import AppRoutes from './routes';
import { Error } from './pages/Error';
import { lightTheme } from './configs/themes';
import { useAuthUser } from './queries/user-query';
import { Loader, LoaderColorProps } from './components/atoms/Loader';
import { getUser, setToken, setUser } from './utils/users';
import { initializeQZTrayCertificate } from './utils/print-label-utils';
/* eslint-enable import/order */

const App = () => {
  const { user, isAuthenticated, isLoading: auth0Loading, error, getAccessTokenSilently } = useAuth0();
  // Start as true so there's no flash between Auth0 finishing and our token fetch starting
  const [isLoading, setIsLoading] = useState(true);
  const hasError = user?.email_verified === false || user?.blocked === true || error;
  const authQuery = useAuthUser(user);
  const [auth0Token, setAuth0Token] = useState('');

  useEffect(() => {
    if (auth0Loading) return; // Still processing Auth0 callback
    if (isAuthenticated) {
      getUserToken();
    } else {
      setIsLoading(false); // Auth0 done, not authenticated — stop loading
    }
  }, [isAuthenticated, auth0Loading]);

  const getUserToken = async () => {
    setIsLoading(true);
    const token = await getAccessTokenSilently();
    if (token) {
      setToken(token);
      setAuth0Token(token);
      setUser(user);
      setIsLoading(false);
    }
  };

  useEffect(() => {
    initializeQZTrayCertificate();
  }, []);

  useEffect(() => {
    const fetchUser = async () => {
      if (auth0Token) {
        const userObj = getUser();
        userObj && (await authQuery.mutateAsync(userObj));
      }
    };
    fetchUser();
  }, [auth0Token]); // authQuery excluded: useMutation returns new object each render, causing infinite loop

  // Show loader while Auth0 is processing the code= callback or fetching the token.
  // This prevents the home page from flashing during the OAuth redirect.
  if (isLoading || auth0Loading) {
    return (
      <Grid mt="200px" justifyContent="center" display="flex">
        <Loader color={LoaderColorProps.Success} />
      </Grid>
    );
  }

  if (hasError) {
    return <Error />;
  }

  return (
    <ThemeProvider theme={lightTheme}>
      <AppRoutes />
    </ThemeProvider>
  );
};

export default App;
